import { useState, useEffect } from "react";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export default function PWAInstallBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia("(display-mode: standalone)").matches) {
      setIsInstalled(true);
      return;
    }
    // Check if dismissed before
    if (localStorage.getItem("pwa-banner-dismissed") === "true") {
      setDismissed(true);
      return;
    }

    // Detect iOS
    const ua = navigator.userAgent;
    const ios = /iphone|ipad|ipod/i.test(ua) && !(window as unknown as { MSStream?: unknown }).MSStream;
    setIsIOS(ios);

    if (ios) {
      // Show iOS instructions after 3s
      setTimeout(() => setShowBanner(true), 3000);
    }

    // Android / Chrome install prompt
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setTimeout(() => setShowBanner(true), 2000);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") {
        setShowBanner(false);
        setIsInstalled(true);
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    setDismissed(true);
    localStorage.setItem("pwa-banner-dismissed", "true");
  };

  if (isInstalled || dismissed || !showBanner) return null;

  return (
    <div className="fixed bottom-20 left-3 right-3 z-50 md:left-auto md:right-6 md:bottom-6 md:w-96 animate-slide-up">
      <div className="bg-white rounded-2xl border border-amber-200 overflow-hidden"
        style={{ boxShadow: "0 8px 32px rgba(0,0,0,0.12)" }}>
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500 to-amber-400 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img
              src="https://static.readdy.ai/image/3fc89005e5f8f541d0781d07714068dd/b31ed3570265bb91107b593ba40da4ad.jpeg"
              alt="CrownGlobal"
              className="w-8 h-8 rounded-lg object-cover"
            />
            <div>
              <p className="text-white font-bold text-sm leading-tight">CrownGlobal</p>
              <p className="text-amber-100 text-xs">Factory &amp; Buyer Platform</p>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="w-6 h-6 flex items-center justify-center text-white/80 hover:text-white cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Body */}
        <div className="px-4 py-3">
          {isIOS ? (
            <>
              <p className="text-gray-800 font-semibold text-sm mb-1">Install on your iPhone / iPad</p>
              <p className="text-gray-500 text-xs mb-3 leading-relaxed">
                Add CrownGlobal to your home screen for the full app experience — works offline too!
              </p>
              <div className="space-y-2">
                {[
                  { icon: "ri-share-forward-line", text: 'Tap the Share button at the bottom of Safari' },
                  { icon: "ri-add-box-line", text: 'Scroll down and tap "Add to Home Screen"' },
                  { icon: "ri-checkbox-circle-line", text: 'Tap "Add" — done! App icon appears on your home screen' },
                ].map((step, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <div className="w-6 h-6 flex items-center justify-center bg-amber-50 rounded-full shrink-0">
                      <i className={`${step.icon} text-amber-500 text-sm`}></i>
                    </div>
                    <p className="text-gray-600 text-xs leading-relaxed">{step.text}</p>
                  </div>
                ))}
              </div>
              <div className="mt-3 bg-amber-50 rounded-lg px-3 py-2 flex items-center gap-2">
                <i className="ri-information-line text-amber-500 text-sm shrink-0"></i>
                <p className="text-amber-700 text-xs">Also works on Huawei, Xiaomi, OPPO &amp; all Chinese phones via browser menu</p>
              </div>
            </>
          ) : (
            <>
              <p className="text-gray-800 font-semibold text-sm mb-1">Install the App — It&apos;s Free!</p>
              <p className="text-gray-500 text-xs mb-3 leading-relaxed">
                Get instant access to millions of factory products. Works on Android, Huawei, Xiaomi, OPPO &amp; all phones.
              </p>
              <div className="flex items-center gap-3 mb-3">
                {[
                  { icon: "ri-flashlight-line", label: "Fast" },
                  { icon: "ri-wifi-off-line", label: "Offline" },
                  { icon: "ri-notification-line", label: "Alerts" },
                ].map((f) => (
                  <div key={f.label} className="flex-1 flex flex-col items-center gap-1 bg-amber-50 rounded-lg py-2">
                    <i className={`${f.icon} text-amber-500 text-base`}></i>
                    <span className="text-gray-600 text-xs">{f.label}</span>
                  </div>
                ))}
              </div>
              <button
                onClick={handleInstall}
                className="w-full bg-amber-400 hover:bg-amber-500 text-white font-bold text-sm py-2.5 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-download-line mr-1.5"></i>
                Install App Now
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
